import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { 
  extractCommunitiesFromPost, 
  ensureCommunitiesExist, 
  linkCommunitiesToLoop,
  trackCommunityInteraction,
  ExtractedCommunity 
} from '@/utils/communityExtraction';
import { getImageLabels } from '@/utils/visionApi';

export interface Community {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  color?: string;
  gradient?: string;
  is_fomo_official: boolean;
  is_auto_generated: boolean;
  member_count: number;
  post_count: number;
}

export interface LoopCommunity {
  id: string;
  loop_id: string;
  community_id: string;
  confidence: number;
  source: 'hashtag' | 'semantic' | 'vision' | 'manual';
  community: Community;
}

/**
 * Hook to fetch communities for a specific loop
 */
export function useLoopCommunities(loopId?: string) {
  return useQuery({
    queryKey: ['loop-communities', loopId],
    queryFn: async () => {
      if (!loopId) return [];
      
      const { data, error } = await supabase
        .from('loop_communities')
        .select(`
          *,
          community:communities(*)
        `)
        .eq('loop_id', loopId)
        .order('confidence', { ascending: false });
      
      if (error) {
        console.error('Error fetching loop communities:', error);
        return [];
      }
      
      // Debug: log the raw data to see what we're getting
      if (data && data.length > 0) {
        console.log('[useLoopCommunities] Raw data:', data);
        console.log('[useLoopCommunities] First item:', data[0]);
        console.log('[useLoopCommunities] First item community:', data[0]?.community);
        console.log('[useLoopCommunities] Community type:', typeof data[0]?.community);
      }
      
      // Handle case where community might be an array from the join
      const normalized = data?.map(item => ({
        ...item,
        community: Array.isArray(item.community) ? item.community[0] : item.community
      })) || [];
      
      return normalized as LoopCommunity[];
    },
    enabled: !!loopId
  });
}

/**
 * Hook to extract and assign communities to a loop
 */
export function useExtractCommunities() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const extractCommunities = useCallback(async (
    loopId: string,
    content: string,
    imageUrl?: string,
    disabledCommunityIds?: Set<string>
  ) => {
    try {
      console.log('[Community Extraction] Starting extraction for loop:', loopId);
      console.log('[Community Extraction] Content length:', content?.length || 0);
      console.log('[Community Extraction] Has image:', !!imageUrl);
      console.log('[Community Extraction] Disabled community IDs received:', {
        size: disabledCommunityIds?.size,
        ids: disabledCommunityIds ? Array.from(disabledCommunityIds) : []
      });
      
      // Extract vision labels if image is provided
      let visionLabels: string[] = [];
      if (imageUrl) {
        console.log('[Community Extraction] Extracting vision labels from image...');
        visionLabels = await getImageLabels(imageUrl);
        console.log('[Community Extraction] Vision labels found:', visionLabels.length);
      }
      
      // Extract communities from content and vision labels
      console.log('[Community Extraction] Extracting communities from text and vision...');
      const result = await extractCommunitiesFromPost(content, visionLabels, imageUrl);
      console.log('[Community Extraction] Communities extracted:', result.communities.length);
      console.log('[Community Extraction] Topics found:', result.topics.length);
      
      // Ensure all communities exist in database
      const communityIdMap = await ensureCommunitiesExist(result.communities);

      if (result.communities.length === 0) {
        console.warn('[Community Extraction] No communities detected for loop – nothing will be linked.', {
          loopId,
          contentPreview: content?.substring(0, 80) || '',
          hasImage: !!imageUrl,
        });
      }

      // Build a quick summary so we can tell if the disabled set is suppressing everything
      const enabledCommunityNames: string[] = [];
      const disabledCommunitySummary: Array<{ name: string; matchedBy: string[] }> = [];

      if (result.communities.length > 0) {
        for (const community of result.communities) {
          const communityId = communityIdMap.get(community.communityName);
          const previewId = `preview-${community.communityName}`;
          const locId = `loc-${community.communityName}`;
          const matchedBy: string[] = [];

          if (communityId && disabledCommunityIds?.has(communityId)) matchedBy.push('id');
          if (disabledCommunityIds?.has(previewId)) matchedBy.push('previewId');
          if (disabledCommunityIds?.has(locId)) matchedBy.push('locationId');
          if (disabledCommunityIds?.has(community.communityName)) matchedBy.push('name');

          if (matchedBy.length > 0) {
            disabledCommunitySummary.push({
              name: community.communityName,
              matchedBy,
            });
          } else {
            enabledCommunityNames.push(community.communityName);
          }
        }

        if (disabledCommunitySummary.length > 0 || enabledCommunityNames.length === 0) {
          console.log('[Community Extraction] Community enablement summary before linking:', {
            loopId,
            extractedCount: result.communities.length,
            enabled: enabledCommunityNames,
            disabled: disabledCommunitySummary,
            disabledSetSize: disabledCommunityIds?.size || 0,
          });

          if (enabledCommunityNames.length === 0 && disabledCommunitySummary.length > 0) {
            console.warn('[Community Extraction] All detected communities are currently disabled – nothing will be linked.', {
              loopId,
              disabled: disabledCommunitySummary,
            });
          }
        }
      }

      // Link communities to the loop (excluding disabled ones)
      await linkCommunitiesToLoop(loopId, result.communities, communityIdMap, disabledCommunityIds);
      
      // Track user posting in these communities (only enabled ones)
      if (user?.id) {
        for (const community of result.communities) {
          const communityId = communityIdMap.get(community.communityName);
          if (communityId && disabledCommunityIds) {
            // BUG FIX: Check both real ID and preview ID formats
            const previewId = `preview-${community.communityName}`;
            const locId = `loc-${community.communityName}`;
            const isDisabled = disabledCommunityIds.has(communityId) || 
                             disabledCommunityIds.has(previewId) ||
                             disabledCommunityIds.has(locId);
            
            if (!isDisabled) {
              await trackCommunityInteraction(user.id, communityId, 'post', 1.5);
            }
          } else if (communityId) {
            // No disabled list provided, track all
            await trackCommunityInteraction(user.id, communityId, 'post', 1.5);
          }
        }
      }
      
      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['loop-communities', loopId] });
      
      // Store extracted topics and vision labels in the loop record
      await supabase
        .from('loops')
        .update({
          extracted_topics: result.topics,
          vision_labels: visionLabels
        })
        .eq('id', loopId);
      
      // Populate loops_topics normalized table for ranking algorithm
      if (result.topics && result.topics.length > 0) {
        const topicRecords = result.topics.map(topic => ({
          loop_id: loopId,
          topic: topic.toLowerCase(),
          confidence: topic.startsWith('#') ? 0.9 : 0.7, // Higher confidence for explicit hashtags
          source: topic.startsWith('#') ? 'hashtag' : 'semantic'
        }));
        
        const { error: topicsError } = await supabase
          .from('loops_topics')
          .upsert(topicRecords, { 
            onConflict: 'loop_id,topic,source',
            ignoreDuplicates: false 
          });
        
        if (topicsError) {
          console.warn('[Community Extraction] Failed to insert topics:', topicsError);
        } else {
          console.log('[Community Extraction] Inserted', topicRecords.length, 'topics for ranking');
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error extracting communities:', error);
      toast({
        title: 'Community Detection Issue',
        description: 'Could not automatically detect communities. You can add them manually.',
        variant: 'default'
      });
      return null;
    }
  }, [user, queryClient, toast]);
  
  return { extractCommunities };
}

/**
 * Hook to track community interactions
 */
export function useCommunityInteraction() {
  const { user } = useAuth();
  
  const trackInteraction = useCallback(async (
    communityId: string,
    interactionType: 'view' | 'like' | 'comment' | 'post'
  ) => {
    if (!user?.id) return;
    
    try {
      await trackCommunityInteraction(user.id, communityId, interactionType);
    } catch (error) {
      console.error('Error tracking community interaction:', error);
    }
  }, [user]);
  
  return { trackInteraction };
}

/**
 * Hook to fetch all available communities
 */
export function useAllCommunities() {
  return useQuery({
    queryKey: ['all-communities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .order('is_fomo_official', { ascending: false })
        .order('post_count', { ascending: false });
      
      if (error) {
        console.error('Error fetching communities:', error);
        return [];
      }
      
      return data as Community[];
    }
  });
}

/**
 * Hook to get FOMO official communities
 */
export function useFomoOfficialCommunities() {
  return useQuery({
    queryKey: ['fomo-official-communities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .eq('is_fomo_official', true)
        .order('name');
      
      if (error) {
        console.error('Error fetching FOMO communities:', error);
        return [];
      }
      
      return data as Community[];
    }
  });
}
